import React from "react";
import { fetchQuery, graphql } from "react-relay";
import type { ActionMeta, GroupBase, OnChangeValue, Options, OptionsOrGroups } from "react-select";
import AsyncCreatable, { type AsyncCreatableProps } from "react-select/async-creatable";

import { faCheck, faTimes as faTimesRegular } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faExclamationTriangle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact, property, throttle } from "lodash";

import ViewportContext from "~/common/ViewportContext";
import { zindexModal } from "~/css/zindexes";
import { isValidEmail } from "~/lib/utils";
import { environment } from "~/models";
import { colors } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import fellowSelectProps from "~/ui/react-select";

import type { EmailListInputContactsQuery as QueryType } from "~/__generated__/EmailListInputContactsQuery.graphql";
import type { ResponseStatusChoices } from "~/__generated__/MeetingsPanelQuery.graphql";

import styles from "./css/EmailListInput.module.css";

export interface Contact {
	id: string;
	email: string;
	fullName?: string;
	image?: string;
	isDisabled?: boolean;
	hasConfiguredUser?: boolean;
	isGuest?: boolean;
	isExternal?: boolean;
	user?: UserAttrs | null;
}

export const ContactsQuery = graphql`
	query EmailListInputContactsQuery(
		$search: String
		$first: Int
		$excludeManagedUsers: Boolean
		$excludeAncestryFor: ID
		$doesNotReportUnder: ID
	) {
		viewer {
			contacts(
				first: $first
				search: $search
				excludeManagedUsers: $excludeManagedUsers
				excludeAncestryFor: $excludeAncestryFor
				doesNotReportUnder: $doesNotReportUnder
				ranked: true
				excludeInactive: true
			) {
				edges {
					node {
						...ContactSearch_contact @relay(mask: false)
						user {
							id
						}
					}
				}
			}
			user {
				id
				email
				fullName
				firstName
				image
				isGuest
			}

			account {
				isPersonalType
				emailDomainList
			}
		}
	}
`;

export type AttendanceRecord = {
	email: string;
	responseStatus: ResponseStatusChoices; // TODO: not directly related to that component, but it seems, that
	// no other way to have a type other than import from any request
};

type ExtraProps = {
	validateDomain?: boolean;
	useNewTeammateBtn?: boolean;
	exclude?: string[];
	internalInvitesAllowed: boolean;
	guestInvitesAllowed: boolean | null;
	externalInvitesAllowed: boolean | null;
	excludeManagedUsers?: boolean;
	excludeAncestryFor?: string;
	doesNotReportUnder?: string;
	guestCount?: number;
	guestLimit?: number;
	externalDiamonds?: boolean;
	isAdmin?: boolean;
	isEventAttendeesInput?: boolean;
	attendanceData?: AttendanceRecord[];
	disabled?: boolean;
};
type EmailListInputProps = Pick<
	AsyncCreatableProps<Contact, true, GroupBase<Contact>>,
	"placeholder" | "onChange" | "value" | "autoFocus" | "isMulti" | "className"
> &
	ExtraProps;

class EmailListInput extends React.Component<EmailListInputProps> {
	domains: string[] = [];
	isPersonal = false;
	static contextType = ViewportContext;
	declare context: React.ContextType<typeof ViewportContext>;

	static defaultProps = {
		internalInvitesAllowed: true,
		guestInvitesAllowed: true,
		externalInvitesAllowed: true,
		externalDiamonds: true,
	};

	constructor(props: EmailListInputProps) {
		super(props);
	}

	loadOptions = (value: string): Promise<OnChangeValue<Contact, true>> => {
		return fetchQuery<QueryType>(environment, ContactsQuery, {
			search: value,
			first: 10,
			excludeManagedUsers: this.props.excludeManagedUsers ?? false,
			excludeAncestryFor: this.props.excludeAncestryFor,
			doesNotReportUnder: this.props.doesNotReportUnder,
		})
			.toPromise()
			.then((result): OnChangeValue<Contact, true> => {
				if (result?.viewer.contacts) {
					const { contacts, user, account } = result.viewer;
					const { exclude } = this.props;

					this.domains = account.emailDomainList.map(s => s);
					this.isPersonal = account.isPersonalType;

					let results: OnChangeValue<Contact, true> = compact(
						contacts.edges.map(edge => (edge && edge.node ? edge.node : null)),
					).map(node => {
						const externalInvite =
							!account.isPersonalType && // otherwise check if the email domains match ignoring case
							!account.emailDomainList.some(domain => node?.email.toLowerCase().endsWith(`@${domain.toLowerCase()}`));

						return {
							id: node.id,
							email: node.email,
							fullName: node.fullName,
							image: node.image,
							hasConfiguredUser: node.user?.isConfigured ?? false,
							isGuest: !!node.user?.isGuest || (!this.props.externalInvitesAllowed && externalInvite), // user the contact user isGuest if available,
							isExternal: externalInvite && !!this.props.externalInvitesAllowed,
							user: node.user,
						};
					});

					if (
						!this.props.internalInvitesAllowed &&
						(this.props.externalInvitesAllowed || this.props.guestInvitesAllowed)
					) {
						results = results.filter(value => value.hasConfiguredUser);
					}

					if (
						user.fullName.toLowerCase().includes(value.toLowerCase()) ||
						user.email.toLowerCase().includes(value.toLowerCase())
					) {
						results = results.concat({
							id: user.id,
							email: user.email,
							fullName: user.fullName,
							image: user.image,
							hasConfiguredUser: true,
							isGuest: false,
							user,
						});
					}

					/* filtering results */
					if (exclude) {
						results = results.filter(re => !exclude.includes(re.email));
					}

					return results;
				} else {
					return [];
				}
			});
	};

	loadOptionsThrottled = throttle(this.loadOptions);

	filterOptions = (option: { label: string; value: string; data: Contact }) => {
		return !this.props.exclude?.includes(option.value);
	};

	getNewOptionEmail = (value: string): string => {
		return !value.includes("@") && !this.isPersonal
			? `${value.toLowerCase()}@${this.domains[0] || "gmail.com"}`
			: value.toLowerCase();
	};

	getNewOptionData = (value: string): Contact => {
		const { validateDomain, exclude, internalInvitesAllowed, externalInvitesAllowed, guestInvitesAllowed } = this.props;
		const email = this.getNewOptionEmail(value).trim();
		const isEmailFromDomain = this.domains.some(domain => email.toLowerCase().endsWith(`@${domain.toLowerCase()}`));
		const externalInvite = isValidEmail(email) && !isEmailFromDomain && !this.isPersonal;
		const isValidDomain = validateDomain ? isEmailFromDomain : true;
		const alreadyAdded = exclude ? exclude.includes(email) : false;
		const isGuest = externalInvite && !externalInvitesAllowed;
		const isExternal = (externalInvite || (isEmailFromDomain && !internalInvitesAllowed)) && !!externalInvitesAllowed;
		const isDisabled =
			!isValidDomain ||
			!isValidEmail(email) ||
			alreadyAdded ||
			(!internalInvitesAllowed && !externalInvite && !externalInvitesAllowed) ||
			(!externalInvitesAllowed && externalInvite && !guestInvitesAllowed);

		return { id: "0", email, isDisabled, hasConfiguredUser: false, isGuest, isExternal };
	};

	shouldDisplayNewOption = (
		inputValue: string,
		value: OnChangeValue<Contact, true>,
		options: OptionsOrGroups<Contact, GroupBase<Contact>>,
	): boolean => {
		const email = this.getNewOptionEmail(inputValue);
		return (
			inputValue.length > 0 && !(options as Options<Contact>).find(contact => contact.email.toLowerCase() === email)
		);
	};

	formatOptionLabel = (contact: Contact, { context }: { context: "menu" | "value" }): React.ReactNode => {
		const { id, fullName, email, image, hasConfiguredUser, isDisabled, isGuest, isExternal } = contact;
		const {
			validateDomain,
			internalInvitesAllowed,
			externalInvitesAllowed,
			guestInvitesAllowed,
			useNewTeammateBtn,
			isEventAttendeesInput,
			attendanceData,
			externalDiamonds,
		} = this.props;

		// CX-456: The check for user.id being equal to zero is for non-participant members.
		//  We did not want to show the diamond for these users and therefore this check
		//  will remove it from their icon. Should we introduce support for non-participants
		//  we can adjust the code below.
		const hideDiamond = !externalDiamonds && contact.id === "0";

		if (context == "menu") {
			const alreadyAdded = this.props.exclude?.includes(email);
			const isDisallowedInternal = validateDomain && isDisabled && !alreadyAdded,
				isDisallowedGuest = !guestInvitesAllowed && isGuest && !hasConfiguredUser && !alreadyAdded,
				isDisallowedExternal = !externalInvitesAllowed && isExternal && !alreadyAdded;
			if (isDisallowedInternal || isDisallowedGuest || isDisallowedExternal) {
				/* it's disabled for an invalid email */
				return (
					<>
						<div className={styles.errorItem}>
							<span>
								<FontAwesomeIcon icon={faExclamationTriangle} />
							</span>
							<p>{email}</p>
						</div>
						{this.domains.length > 0 && (
							<p className={styles.errorMsg}>Must be a valid email ending in {this.domains.join(", ")}</p>
						)}
						{this.domains.length == 0 && <p className={styles.errorMsg}>User not found</p>}
					</>
				);
			}

			if (!hasConfiguredUser && !internalInvitesAllowed && !isExternal && !isGuest) {
				return (
					<>
						<div className={styles.errorItem}>
							<span>
								<FontAwesomeIcon icon={faExclamationTriangle} />
							</span>
							<p>{email}</p>
						</div>
						<p className={styles.errorMsg}>Only external valid email invitations are enabled</p>
					</>
				);
			}
			if ((isExternal && !externalInvitesAllowed) || (!hasConfiguredUser && isGuest && !guestInvitesAllowed)) {
				return (
					<>
						<div className={styles.errorItem}>
							<span>
								<FontAwesomeIcon icon={faExclamationTriangle} />
							</span>
							<p>{email}</p>
						</div>
						<p className={styles.errorMsg}>External invites are not allowed</p>
					</>
				);
			}

			const newLabel = isGuest ? "+ New guest" : isExternal ? "+ New external" : "+ New teammate";
			/* it might be disabled if email was added already */
			return (
				<div className={cx(styles.item, { [styles.disabled]: isDisabled })}>
					<Avatar
						link={false}
						width="2em"
						user={{ id, fullName: fullName || email, image: image, isGuest: false, isExternal }}
						className={styles.icon}
						hideDiamond={hideDiamond}
					/>
					<div className={styles.content}>
						<div>{fullName || email}</div>
						<small>{email}</small>
					</div>
					{useNewTeammateBtn && !hasConfiguredUser && !this.isPersonal && (
						<p className={styles.plusBtn}>{isDisabled && alreadyAdded ? "Already added" : newLabel}</p>
					)}
				</div>
			);
		} else if (isEventAttendeesInput) {
			const attendanceRecord = attendanceData?.find(att => att.email === email);
			let sign: React.ReactNode = "";

			if (attendanceRecord?.responseStatus === "accepted") {
				sign = <FontAwesomeIcon icon={faCheck} />;
			} else if (attendanceRecord?.responseStatus === "declined") {
				sign = <FontAwesomeIcon icon={faTimesRegular} />;
			} else if (["tentative", "tentativeAccepted"].includes(attendanceRecord?.responseStatus || "")) {
				sign = "?";
			}

			return (
				<p className={cx(styles.multiValueText, styles.attendeesText)}>
					<Avatar
						user={contact}
						hideDiamond={hideDiamond}
						link={false}
						width="28px"
						className={styles.attendeeAvatar}
					/>
					<span title={fullName || email}>{fullName || email}</span>
					{attendanceRecord && sign && (
						<span
							className={cx(styles.responseStatusCircle, {
								[styles.accepted]: attendanceRecord.responseStatus === "accepted",
								[styles.declined]: attendanceRecord.responseStatus === "declined",
								[styles.tentative]: ["tentative", "tentativeAccepted"].includes(attendanceRecord.responseStatus),
							})}
						>
							{sign}
						</span>
					)}
				</p>
			);
		} else {
			return (
				<p className={cx(styles.multiValueText, styles.attendeesText)}>
					<Avatar
						user={contact}
						hideDiamond={hideDiamond}
						link={false}
						width="28px"
						className={styles.attendeeAvatar}
					/>
					<span title={fullName || email}>{fullName || email}</span>
				</p>
			);
		}
	};

	onValueChange =
		(onChangeProp?: (value: OnChangeValue<Contact, true>, action: ActionMeta<Contact>) => void) =>
		(value: OnChangeValue<Contact, true>, action: ActionMeta<Contact>) => {
			onChangeProp?.(value, action);
		};

	renderDesktop() {
		const { useNewTeammateBtn, validateDomain, onChange: onChangeProp, ...rest } = this.props;

		return (
			<>
				<AsyncCreatable<Contact, true>
					{...fellowSelectProps}
					components={{
						IndicatorSeparator: null,
						DropdownIndicator: null,
					}}
					loadOptions={this.loadOptionsThrottled}
					cacheOptions
					defaultOptions
					getOptionValue={property("email")}
					formatOptionLabel={this.formatOptionLabel}
					isClearable={false}
					allowCreateWhileLoading={false}
					getNewOptionData={this.getNewOptionData}
					isValidNewOption={this.shouldDisplayNewOption}
					filterOption={this.filterOptions}
					isDisabled={this.props.disabled}
					menuPosition="fixed"
					menuPortalTarget={document.body}
					styles={{
						container: provided => ({
							...provided,
							"flex-grow": "1",
							"min-width": 0,
						}),
						menuPortal: provided => ({ ...provided, zIndex: zindexModal + 1 }),
						control: provided => ({
							...provided,
							border: "transparent",
							boxShadow: "none",
							padding: "2px 4px",
							cursor: "text",
						}),
						input: provided => ({
							...provided,
							paddingTop: 0,
							paddingBottom: 0,
						}),
						valueContainer: provided => ({
							...provided,
							padding: "0",
						}),
						multiValue: provided => {
							let bgColor = "var(--thirdLayerColor)";
							let color = "var(--vividGrey)";

							return {
								...provided,
								background: bgColor,
								color: color,
								borderRadius: "14px",
								cursor: "default",
								position: "relative",
								paddingRight: "25px",
							};
						},
						multiValueLabel: (provided, state) => {
							let color = "var(--tertiaryTextColor)";

							if (useNewTeammateBtn && !state.data.hasConfiguredUser) {
								color = colors.royal[600];
							}

							return {
								...provided,
								color: color,
								padding: "6px",
								paddingLeft: "9px",
							};
						},
						multiValueRemove: provided => ({
							...provided,
							cursor: "pointer",
							backgroundColor: "transparent",
							position: "absolute",
							right: "4px",
							top: "4px",
							height: "21px",
							borderRadius: "50%",
							paddingLeft: "3.5px",
							paddingRight: "3.5px",

							":hover": {
								background: "var(--mutedGrey)",
								color: "var(--placeholderTextColor)",
							},
						}),
						option: (provided, state) => ({
							...provided,
							padding: state.isDisabled && validateDomain ? "0px" : "8px",
							margin: "4px 8px",
							borderRadius: "6px",
							width: "initial",
						}),
						placeholder: provided => ({
							...provided,
							fontSize: "0.9rem",
							color: "var(--secondaryTextColor)",
						}),
					}}
					// eslint-disable-next-line react/jsx-no-bind
					onChange={this.onValueChange(onChangeProp)}
					{...rest}
				/>
			</>
		);
	}

	render() {
		return this.renderDesktop();
	}
}

export default EmailListInput;
